<template>
    <div class="vx-col-span-12 lg:vx-col-span-10">
        <page-header class="vx-mb-2 md:vx-mb-2"><slot name="page-header">Users</slot></page-header>

        <group-users-controls
            v-model="fetchParams"
            @apply="applyFilters"
            @reset="resetFilters"
            @add="openUserModal()"
            :sortOptions="[
                {
                    value: 'added_at',
                    label: 'Sort: Added at',
                },
            ]"
            :roleOptions="groupRolesOptions()"
            :groupId="props.groupId"
            :loading="loading">
        </group-users-controls>

        <div v-if="loading && !list.length" class="vx-text-center vx-py-10">
            <vx-icon-loading class="vx-w-9 vx-h-9 vx-animate-loading-spin"></vx-icon-loading>
        </div>

        <template v-else-if="!loading && list.length === 0">
            <vx-box color="info"
                v-if="!isEqual(defaultFilters(), fetchParams)"
                class="vx-mt-4 vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500">
                <div class="vx-w-64 vx-mx-auto vx-text-center">
                    No results for current search, try adjusting your filters.
                </div>
                <vx-button type="button" color="secondary" @click="resetFilters">
                    Reset All <font-awesome-icon :icon="faCircleX" />
                </vx-button>
            </vx-box>
            <vx-box v-else color="info" class="vx-mt-4 vx-flex vx-flex-col vx-px-8 vx-py-20 vx-gap-6 vx-items-center vx-text-xl vx-text-slate-500">
                No users yet.
                <vx-button v-if="onCorporate() || ['admin'].includes(roleOnCurrentGroup)" @click.prevent="openUserModal()">
                    Add User <font-awesome-icon :icon="faPlusCircle" />
                </vx-button>
            </vx-box>
        </template>
        <template v-else>
            <vx-table v-if="list.length" class="xl:vx-grid-cols-4 vx-mt-0" rowBreakpoint="xl">
                <vx-table-row isHeader>
                    <div class="xl:vx-col-span-2">
                        Name
                    </div>
                    <div>
                        Role
                    </div>
                </vx-table-row>
                <group-user-row v-for="(user, index) in list"
                    :modelValue="list[index]"
                    :key="list[index].id"
                    :groupId="props.groupId"
                    @edit="openUserModal(list[index])"
                    @deleted="removeFromList(list[index].id)"></group-user-row>
            </vx-table>

            <div
                v-if="list.length"
                class="vx-mt-8 vx-flex vx-justify-between vx-w-full vx-gap-10"
            >
                <vx-button
                    v-if="fetchParams.cursor"
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    class="vx-grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                    <font-awesome-icon :icon="faSpinner"></font-awesome-icon>
                </vx-button>

                <vx-alert color="slate" :closeable="false">
                    <template v-slot:icon><font-awesome-icon :icon="faCircleInfo" /></template>
                    Corporate owner or administrator users with implicit access will not be duplicated here.
                </vx-alert>
            </div>
        </template>
        <group-user-form-modal v-model="selectedUser" v-model:visible="showUserModal" :groupId="props.groupId" @added="userAdded" @updated="userUpdated"></group-user-form-modal>
    </div>
</template>
<script setup>
import {
    VxAlert,
    VxBox, VxButton,
    VxIconLoading,
    VxTable,
    VxTableRow,
} from '@voxie/frontend-components';
import PageHeader from '~/components/general/PageHeader.vue';
import { onMounted, ref } from 'vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import groupUsersClient from '~/services/groupUsersClient';
import GroupUsersControls from './GroupUsersControls.vue';
import GroupUserRow from './GroupUserRow.vue';
import GroupUserFormModal from '~/components/settings/teams/groups/form/users/GroupUserFormModal.vue';
import { DEFAULT_GROUP_ROLE, groupRolesOptions } from '~/constants/roles';
import {
  faCircleInfo,
  faCircleX, faPlusCircle,
  faSpinner,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { isEqual } from '~/utils/helpers';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const teamId = Spark.state.currentTeam.id;
const { list, loading, fetchParams, fetchRecords } = useCursorPagination();
const { onCorporate, roleOnCurrentGroup } = useCurrentGroup();

const props = defineProps({
    groupId: {
        type: String,
        required: false,
    },
});

let defaultFilters = () => ({
    query: '',
    per_page: 10,
    cursor: null,
    sort_field: 'added_at',
    sort_dir: 'desc',
    role: null,
});

const defaultUser = () => {
    return {
        id: null,
        name: '',
        email: '',
        role: DEFAULT_GROUP_ROLE,
    };
};

const selectedUser = ref(defaultUser());

const showUserModal = ref(false);

const resetFilters = () => {
    fetchParams.value = defaultFilters();

    loadData();
};

const applyFilters = () => {
    fetchParams.value = {
        ...fetchParams.value,
        cursor: null,
    }

    loadData();
};

const removeFromList = (itemId) => {
    list.value = list.value.filter((item) => item.id !== itemId);

    if (!list.value.length) {
        loadData();
    }
};

const loadData = () => {
    fetchRecords(groupUsersClient.listGroupUsers(teamId, props.groupId, fetchParams.value));
};

const userAdded = (user) => {
    list.value = [
        { ...user },
        ...list.value
    ];
};

const userUpdated = (updatedUser) => {
    list.value = list.value.map((user) => {

        if (user.id === updatedUser) {
            return {
                ...user,
                role: selectedUser.value.role,
            };
        }

        return user;
    })
};

const openUserModal = (user = null) => {
    selectedUser.value = user ? { ...user } : defaultUser();
    showUserModal.value = true;
};

onMounted(() => {
    resetFilters();
});
</script>
